import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AssistantTopBar from './AssistantTopBar';
import { logout } from '../lib/auth';
import { API_BASE_URL } from '../config';
import { formatCurrency, calculateBatchValue } from '../utils/formatUtils';

const TopBar = ({ toggleSidebar, isDarkMode, toggleDarkMode, onOpenMobile, showMenuButton = true, isSidebarExpanded = true }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [sktOpen, setSktOpen] = useState(false);
  const [rescuedOpen, setRescuedOpen] = useState(false);
  const [israfOpen, setIsrafOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [valueOpen, setValueOpen] = useState(false);

  const profileRef = useRef(null);
  const profileMobileRef = useRef(null);
  const sktRef = useRef(null);
  const rescuedRef = useRef(null);
  const israfRef = useRef(null);
  const inventoryRef = useRef(null);
  const valueRef = useRef(null);

  const [avatar, setAvatar] = useState(null);
  const [user, setUser] = useState(null);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [wastedStats, setWastedStats] = useState({ totalWasted: 0, count: 0 });
  const [wastedItemsList, setWastedItemsList] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [hasBaby, setHasBaby] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [isAutoOpen, setIsAutoOpen] = useState(false);
  const [isAssistantChef, setIsAssistantChef] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [sharedListCount, setSharedListCount] = useState(0);
  const [activeListCount, setActiveListCount] = useState(0);
  const dashboardRef = useRef(null);
  const dashboardToggleRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const chefFirstName = useMemo(() => {
    const raw = String(user?.name || user?.email || '').trim();
    if (!raw) return 'Kullanıcı';

    let first = raw.split(/\s+/)[0];
    if (first.includes('@')) first = first.split('@')[0];

    // Handle names like "OzgurAdmin" -> "Ozgur"
    first = first.replace(/([a-zçğıöşü])([A-ZÇĞİÖŞÜ]).*$/, '$1');
    // Handle suffixes like "ozguradmin", "ozgursef"
    first = first.replace(/(admin|chef|sef|şef)$/i, '');
    // Keep only letters
    first = first.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, '');

    if (!first) return 'Kullanıcı';
    return first.charAt(0).toUpperCase() + first.slice(1);
  }, [user?.name, user?.email]);

  const modalData = {
    envanter: {
      title: "Mutfak Stoğu",
      icon: "🍅",
      desc: `Şefim; şu an mutfağında ${inventoryCount} parça malzeme var. Senin için hepsinin SKT'sini, fiyat ve miktarını anlık olarak takip ediyoruz.`
    },
    israf: {
      title: "İsraf Alarmı!",
      icon: "⚠️",
      desc: "Şefim bu rakam tehlikeli! Malzeme çöpe gitmek üzere. Hemen akıllı bildirimleri kullanarak bir 'Kurtarma Tarifi' yaratarak puanınızı koruyabilirsiniz."
    },
    tasarruf: {
      title: "Akıllı Tasarruf",
      icon: "♻️",
      desc: "Bravo Şef! Dönüştürdüğün ürünlerle mutfağında muazzam bir tasarruf yaptın."
    },
    skt: {
      title: "SKT Alarmı",
      icon: "⚠️",
      desc: "Şu an mutfağında son kullanma tarihi yaklaşan ürünler var. Hemen aksiyon alarak israfın önüne geçebilirsin!"
    },
    bebek: {
      title: "Bebek Zırhı Aktif",
      icon: "👶",
      desc: "Yeşil ışık yanıyorsa güvendesin! Bebek için alerjen kontrolü devrede. Sisteme giren her veri bebek filtresinden geçiyor."
    },
    kalkan: {
      title: "Mutfak Güvenlik Zırhı",
      icon: "🛡️",
      desc: "Gıda güvenliği ve alerjen kalkanı devrede. Sistem tüm girişleri denetliyor ve en sağlıklı ortamı stabilize ediyor."
    },
    deger: {
      title: "Değer Analizi",
      icon: "💰",
      desc: "Mutfak ekonomisi anlık takipte. Mutfağındaki ürünlerin maliyeti ve kurtardığın değerlerin toplamı burada yazıyor."
    }
  };

  const openModal = (type) => {
    setModalContent({ ...modalData[type], type });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);



  // Refactored Auto-open Logic (Client-Side Only & Session Isolated)
  useEffect(() => {
    // Only target mobile/tablet where dashboard actually toggleable
    if (window.innerWidth >= 768) return;

    // Wait for user to be loaded to ensure valid session context
    if (!user) return;

    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    const lastAutoOpenTime = sessionStorage.getItem('lastAutoOpenTime');
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;

    // CRITICAL: Strict Flag-Kill (Always remove immediately to prevent loops)
    if (justLoggedIn) {
      sessionStorage.removeItem('justLoggedIn');
    }

    let shouldOpen = false;

    if (justLoggedIn === 'true') {
      // Scenario 1: Fresh Login
      // console.log('🟢 User just logged in! Opening dashboard...');
      shouldOpen = true;
    } else {
      // Scenario 2: Session Timeout (5 Minutes)
      // Check if enough time passed since last auto-open IN THIS TAB
      if (!lastAutoOpenTime || (now - parseInt(lastAutoOpenTime, 10) > FIVE_MINUTES)) {
        // console.log('⏰ Session gap detected (>5m). Re-opening dashboard...');
        shouldOpen = true;
      }
    }

    if (shouldOpen) {
      setDashboardOpen(true);
      setIsAutoOpen(true);
      // Update timestamp for this tab
      sessionStorage.setItem('lastAutoOpenTime', now.toString());
    }
  }, [user?.id]); // Depend only on ID to avoid object reference loops

  // Timer logic for auto-open
  useEffect(() => {
    if (isAutoOpen && dashboardOpen) {
      // console.log('⏳ Auto-close timer started: 10 seconds');
      const timer = setTimeout(() => {
        // console.log('⌛ Timer expired: Closing dashboard');
        setDashboardOpen(false);
        setIsAutoOpen(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isAutoOpen, dashboardOpen]);

  useEffect(() => {
    // Listen for profile updates to refresh data without page reload
    const handleProfileUpdate = () => {
      console.log('🔄 Profile update detected, refreshing TopBar data...');
      fetchData();
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    const handleFocusKpi = (event) => {
      const type = String(event?.detail?.type || '').trim();
      if (!type) return;
      if (!modalData[type]) return;
      openModal(type);
    };

    window.addEventListener('dashboard:focus-kpi', handleFocusKpi);
    return () => window.removeEventListener('dashboard:focus-kpi', handleFocusKpi);
  }, []);

  // Helper to close all dropdowns
  const closeAllDropdowns = () => {
    setProfileOpen(false);
    setSktOpen(false);
    setRescuedOpen(false);
    setIsrafOpen(false);
    setInventoryOpen(false);
    setValueOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target) &&
        profileMobileRef.current && !profileMobileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
      if (sktRef.current && !sktRef.current.contains(event.target)) setSktOpen(false);
      if (rescuedRef.current && !rescuedRef.current.contains(event.target)) setRescuedOpen(false);
      if (israfRef.current && !israfRef.current.contains(event.target)) setIsrafOpen(false);
      if (inventoryRef.current && !inventoryRef.current.contains(event.target)) setInventoryOpen(false);
      if (valueRef.current && !valueRef.current.contains(event.target)) setValueOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Separate Effect for Mobile Dashboard Click Outside (To avoid stale state)
  useEffect(() => {
    if (!dashboardOpen || modalOpen) return;

    const handleDashboardClickOutside = (event) => {
      if (window.innerWidth < 768) {
        if (dashboardRef.current && !dashboardRef.current.contains(event.target) &&
          dashboardToggleRef.current && !dashboardToggleRef.current.contains(event.target)) {
          console.log('📉 Clicking outside mobile dashboard, closing...');
          setDashboardOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleDashboardClickOutside);
    return () => document.removeEventListener("mousedown", handleDashboardClickOutside);
  }, [dashboardOpen, modalOpen]);

  const toggleSkt = (e) => { e.stopPropagation(); closeAllDropdowns(); setSktOpen(!sktOpen); };
  const toggleRescued = (e) => { e.stopPropagation(); closeAllDropdowns(); setRescuedOpen(!rescuedOpen); };
  const toggleIsraf = (e) => { e.stopPropagation(); closeAllDropdowns(); setIsrafOpen(!israfOpen); };
  const toggleInventory = (e) => { e.stopPropagation(); closeAllDropdowns(); setInventoryOpen(!inventoryOpen); };
  const toggleValue = (e) => { e.stopPropagation(); closeAllDropdowns(); setValueOpen(!valueOpen); };

  const toggleProfile = (e) => {
    e.stopPropagation();
    setProfileOpen(!profileOpen);
  };

  const hasPremiumAccess = (data) => {
    if (!data || typeof data !== 'object') return false;
    return Boolean(
      data.isPremium ||
      data.hasAllergenPremium ||
      data.hasDietPremium ||
      data.hasAthletePremium ||
      data.hasAlakartPremium ||
      data.hasSpecialDaysPremium
    );
  };

  const fetchData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setIsPremiumUser(false);
        return;
      }
      const headers = { Authorization: `Bearer ${authToken}` };
      let premiumDetected = false;

      const req = { headers, credentials: 'include' };
      const [meRes, prRes, invRes, wasteRes, wasteListRes, notifRes] = await Promise.all([
        fetch(`${API_BASE_URL}/auth/me`, req),
        fetch(`${API_BASE_URL}/api/profile`, req),
        fetch(`${API_BASE_URL}/api/inventory`, req),
        fetch(`${API_BASE_URL}/api/inventory/wasted-stats`, req),
        fetch(`${API_BASE_URL}/api/inventory/wasted-history`, req),
        fetch(`${API_BASE_URL}/api/notifications`, req)
      ]);

      // Only treat session as invalid when identity endpoints fail.
      // Some feature endpoints may return 401/403 due to module-level guards and must not force logout.
      const hardAuthFailed =
        meRes.status === 401 ||
        (prRes.status === 401 && meRes.status >= 400);

      if (hardAuthFailed) {
        localStorage.removeItem('authToken');
        setUser(null);
        setAvatar(null);
        setIsPremiumUser(false);
        return;
      }

      if (meRes.ok) {
        const u = await meRes.json();
        if (u) {
          setUser(u);
          if (u.avatar) setAvatar(u.avatar);
          premiumDetected = premiumDetected || hasPremiumAccess(u);
        }
      }

      if (prRes.ok) {
        const data = await prRes.json();
        premiumDetected = premiumDetected || hasPremiumAccess(data) || hasPremiumAccess(data?.user);
        if (data?.intakeSurvey?.avatar) setAvatar(data.intakeSurvey.avatar);
        // Initial hasBaby from own profile
        let babyStatus = data?.hasBaby || false;

        // If they are an assistant chef, the baby info might be on the partner's profile
        if (data?.isAssistantChef || data?.role === 'assistant') {
          try {
            const partnerRes = await fetch(`${API_BASE_URL}/api/profile/partner`, { headers, credentials: 'include' });
            if (partnerRes.ok) {
              const partnerData = await partnerRes.json();
              if (partnerData?.partner?.hasBaby) babyStatus = true;
            }
          } catch (e) {
            console.warn('Failed to fetch partner baby status', e);
          }
        }
        setHasBaby(babyStatus);
      }

      // Also check status endpoint for reliable role detection
      const statusRes = await fetch(`${API_BASE_URL}/api/profile/status`, { headers, credentials: 'include' });
      if (statusRes.ok) {
        const status = await statusRes.json();
        premiumDetected =
          premiumDetected ||
          hasPremiumAccess(status) ||
          hasPremiumAccess(status?.user) ||
          Boolean(
            status?.entitlements?.modules?.global?.granted ||
            status?.entitlements?.modules?.allergen?.granted ||
            status?.entitlements?.modules?.diet?.granted ||
            status?.entitlements?.modules?.athlete?.granted ||
            status?.entitlements?.modules?.alakart?.granted ||
            status?.entitlements?.modules?.special_days?.granted
          );
        if (status.isAssistantChef) {
          setIsAssistantChef(true);
          // Re-verify baby status if profile check didn't catch it
          if (!hasBaby) {
            const partnerRes = await fetch(`${API_BASE_URL}/api/profile/partner`, { headers });
            if (partnerRes.ok) {
              const partnerData = await partnerRes.json();
              if (partnerData?.partner?.hasBaby) setHasBaby(true);
            }
          }
        }
      }

      if (invRes.ok) {
        const data = await invRes.json();
        setAllInventoryData(data.inventory || []);
        setInventoryCount(data.inventory?.length || 0);
      }

      if (wasteRes.ok) {
        const data = await wasteRes.json();
        setWastedStats(data);
      }

      if (wasteListRes.ok) {
        const data = await wasteListRes.json();
        setWastedItemsList(data.history || []);
      }

      if (notifRes.ok) {
        const data = await notifRes.json();
        const unread = data.notifications?.filter(n => !n.isRead).length || 0;
        setUnreadCount(unread);
      }
      // Aktif (pending) alışveriş listesi sayısı
      try {
        const listRes = await fetch(`${API_BASE_URL}/api/shopping/lists/counts`, { headers });
        if (listRes.ok) {
          const counts = await listRes.json();
          setActiveListCount(Number(counts?.pendingCount || 0));
        }
      } catch (e) { console.warn('Active list count fetch failed', e); }

      setIsPremiumUser(premiumDetected);
    } catch (e) {
      console.warn('TopBar data load failed', e);
    }
  };

  const kitchenValueStats = useMemo(() => {
    let stats = {
      activeInventoryValue: 0,
      rescuedValue: 0,
      totalValue: 0
    };

    (allInventoryData || []).forEach(item => {
      const val = calculateBatchValue(item);
      if (item.is_rescued === true || val < 0) {
        stats.rescuedValue += Math.abs(val);
      } else if (!isNaN(val)) {
        stats.activeInventoryValue += val;
      }
    });

    stats.totalValue = stats.activeInventoryValue + stats.rescuedValue;
    return stats;
  }, [allInventoryData]);

  const expiringSoonItems = useMemo(() => {
    return (allInventoryData || [])
      .filter(item => item.expiry_date) // Sadece SKT'si olanları al
      .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date)); // En yakından en uzağa sırala
  }, [allInventoryData]);

  const expiringSoonCount = useMemo(() => {
    return (allInventoryData || []).filter(item => {
      if (!item.expiry_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const exp = new Date(item.expiry_date);
      exp.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 3;
    }).length;
  }, [allInventoryData]);

  const criticalStockItems = useMemo(() => {
    return (allInventoryData || [])
      .filter(item => {
        const amount = parseFloat(item.remaining_amount);
        const unit = (item.unit || '').toLowerCase().trim();

        // [RULE UN-02] Unit aware logic for critical threshold
        let normalized = amount;
        if (['kg', 'kilo', 'lt', 'litre'].includes(unit)) {
          normalized = amount * 1000;
        }

        // Precise thresholds: 200g/ml or 2 pieces (discrete)
        const isDiscrete = ['adet', 'tane', 'paket', 'bulb', 'diş', 'bulb'].some(u => unit.includes(u));
        const threshold = isDiscrete ? 2 : 200;

        return amount > 0 && normalized < threshold;
      })
      .slice(0, 4); // Show top 4 critical items
  }, [allInventoryData]);

  const rescuedItems = useMemo(() => {
    return (allInventoryData || []).filter(item => item.is_rescued === true || Number(item.purchase_price) < 0)
      .sort((a, b) => new Date(b.created_at || b.purchase_date) - new Date(a.created_at || a.purchase_date));
  }, [allInventoryData]);

  const rescuedCount = rescuedItems.length;
  const totalSavedValue = useMemo(() => {
    return rescuedItems.reduce((acc, item) => {
      const price = Number(item.purchase_price);
      if (!Number.isFinite(price)) return acc;
      return acc + Math.abs(price);
    }, 0);
  }, [rescuedItems]);

  useEffect(() => {
    const handleShoppingCountUpdated = (event) => {
      const nextCount = Number(event?.detail?.pendingCount);
      if (Number.isFinite(nextCount) && nextCount >= 0) {
        setActiveListCount(nextCount);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000);
    window.addEventListener('refreshNotifications', fetchData);
    window.addEventListener('shoppingListUpdated', fetchData);
    window.addEventListener('Alakart_SYNC', fetchData);
    window.addEventListener('shopping:list-count-updated', handleShoppingCountUpdated);
    window.addEventListener('realtime:notifications-updated', fetchData);
    window.addEventListener('realtime:inventory-updated', fetchData);
    window.addEventListener('realtime:pricing-updated', fetchData);
    window.addEventListener('realtime:force-refetch', fetchData);
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotifications', fetchData);
      window.removeEventListener('shoppingListUpdated', fetchData);
      window.removeEventListener('Alakart_SYNC', fetchData);
      window.removeEventListener('shopping:list-count-updated', handleShoppingCountUpdated);
      window.removeEventListener('realtime:notifications-updated', fetchData);
      window.removeEventListener('realtime:inventory-updated', fetchData);
      window.removeEventListener('realtime:pricing-updated', fetchData);
      window.removeEventListener('realtime:force-refetch', fetchData);
    };
  }, []);

  const isAdminPath = location.pathname.startsWith('/admin');
  const openMobile = onOpenMobile || toggleSidebar || (() => { });

  // Compute the left offset for the fixed header based on sidebar state
  // In Overlay mode, this is static at the collapsed sidebar width (5rem / 20 units)
  const headerLeftClass = 'md:left-20';

  function adminTitle() {
    if (location.pathname === '/admin' || location.pathname === '/admin/') return 'Genel Bakış';
    if (location.pathname.startsWith('/admin/users')) return 'Kullanıcılar';
    if (location.pathname.startsWith('/admin/surveys')) return 'Anket Verileri';
    if (location.pathname.startsWith('/admin/recipes')) return 'Tarifler';
    if (location.pathname.startsWith('/admin/notifications')) return 'Bildirim Yönetimi';
    if (location.pathname.startsWith('/admin/pricing')) return 'Fiyat İstihbaratı';
    if (location.pathname.startsWith('/admin/config')) return 'Sistem Ayarları';
    return 'Yönetim';
  }

  if (isAdminPath) {
    return (
      <header className="fixed top-0 left-0 right-0 z-[110] bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-all duration-300 w-full hidden md:block">
        <div className="mx-auto w-full h-[90px] px-8">
          <div className="flex h-full items-center justify-between gap-12">
            <div className="flex items-center gap-4">
              <button className="md:hidden p-2 rounded-full text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors" onClick={openMobile}>
                <i className="fa-solid fa-bars text-lg"></i>
              </button>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">{adminTitle()}</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">search</span>
                <input className="pl-10 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-64 text-gray-900 dark:text-white transition-colors" placeholder="Ara..." type="text" />
              </div>
              <button onClick={() => navigate('/admin/notifications')} className="relative size-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-primary/10 hover:text-primary transition-colors">
                <span className="material-symbols-outlined">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-1 ring-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
    );
  }
  if (isAssistantChef) {
    return (
      <AssistantTopBar
        user={user}
        avatar={avatar}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        hasBaby={hasBaby}
        openModal={openModal}
        unreadCount={unreadCount}
        sharedListCount={sharedListCount}
        logout={logout}
      />
    );
  }

  return (
    <>
      <style>{`
        @keyframes blink-warning {
            0%, 100% { background-color: rgba(254, 215, 170, 0.5); transform: scale(1); }
            50% { background-color: rgba(249, 115, 22, 0.2); transform: scale(0.98); }
        }
        @keyframes blink-danger {
            0%, 100% { background-color: rgba(254, 202, 202, 0.5); transform: scale(1); }
            50% { background-color: rgba(239, 68, 68, 0.2); transform: scale(0.98); }
        }
        @keyframes blink-success {
            0%, 100% { background-color: rgba(209, 250, 229, 0.5); transform: scale(1); }
            50% { background-color: rgba(16, 185, 129, 0.2); transform: scale(0.98); }
        }
        .animate-skt-blink { animation: blink-warning 1.5s ease-in-out 5; }
        .animate-israf-blink { animation: blink-danger 1.5s ease-in-out 5; }
        .animate-rescued-blink { animation: blink-success 1.5s ease-in-out 5; }
        .modal-overlay { backdrop-filter: blur(8px); background-color: rgba(0,0,0,0.4); }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes blink-5s {
            0%, 100% { opacity: 1; color: #EF4444; }
            50% { opacity: 0.3; color: #B91C1C; }
        }
        @keyframes blink-orange-5s {
            0%, 100% { opacity: 1; color: #F97316; }
            50% { opacity: 0.3; color: #EA580C; }
        }
        @keyframes blink-green-5s {
            0%, 100% { opacity: 1; color: #10B981; }
            50% { opacity: 0.3; color: #059669; }
        }
        .animate-blink-5s { animation: blink-5s 1s ease-in-out 5; }
        .animate-blink-orange-5s { animation: blink-orange-5s 1s ease-in-out 5; }
        .animate-blink-green-5s { animation: blink-green-5s 1s ease-in-out 5; }
      `}</style>

      {/* WEB VIEW HEADER */}
      <header className={`hidden md:flex fixed top-0 right-0 z-[110] h-20 items-center border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-[#1a1a1a] shadow-sm transition-all duration-300 ${headerLeftClass}`}>
        <div className="w-full h-full">
          <div className="mx-auto max-w-7xl flex items-center justify-between h-full px-2 md:px-10">

            {/* Left section: Stats badges grouped */}
            <div className="flex items-center gap-2 lg:gap-3">
              {!isAssistantChef && (
                <div className="relative" ref={inventoryRef}>
                  <div onClick={toggleInventory} className="group flex flex-col items-center justify-center rounded-xl bg-gray-50 dark:bg-slate-800/50 px-3 py-1 border border-gray-100 dark:border-slate-700 min-w-[85px] cursor-pointer hover:bg-white dark:hover:bg-slate-800 hover:shadow-md transition-all text-center">
                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Envanter</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-base">🍅</span>
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{inventoryCount}</span>
                    </div>
                  </div>
                  {inventoryOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-[#1f2937] rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mutfak Stoğu</span>
                        <button onClick={() => navigate('/my-kitchen')} className="text-[10px] font-bold text-primary hover:underline">Tümünü Gör</button>
                      </div>

                      {criticalStockItems.length > 0 ? (
                        <div className="mb-4">
                          <span className="text-[10px] font-bold text-red-500 uppercase tracking-tight block mb-2">Kritik Azalanlar ⚠️</span>
                          <div className="flex flex-col gap-2">
                            {criticalStockItems.map(item => (
                              <div key={item.id} className="flex items-center justify-between p-2 bg-red-50/30 dark:bg-red-900/10 rounded-xl border border-red-100/50 dark:border-red-900/20">
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2">{item.ingredient_name || item.ingredient?.name}</span>
                                <span className="text-[10px] text-red-600 font-black whitespace-nowrap">{Math.round(item.remaining_amount)}{item.unit}</span>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={() => navigate('/my-shopping-lists')}
                            className="w-full mt-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[14px]">shopping_cart</span>
                            Eksikleri Tamamla
                          </button>
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-500 dark:text-gray-400 font-medium leading-relaxed mb-1">{modalData.envanter.desc}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isAssistantChef && (
                <>
                  {/* SKT Alarm Dropdown */}
                  <div className="relative" ref={sktRef}>
                    <div
                      onClick={toggleSkt}
                      className={`flex flex-col items-center justify-center rounded-xl px-3 py-1 border min-w-[85px] text-center cursor-pointer hover:shadow-md transition-all ${expiringSoonCount > 0 ? 'bg-orange-50/50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-900/30 animate-skt-blink' : 'bg-gray-50 dark:bg-slate-800/50 border-gray-100 dark:border-slate-700'}`}
                    >
                      <span className={`text-[9px] font-bold uppercase tracking-wider ${expiringSoonCount > 0 ? 'text-orange-600/70 dark:text-orange-400' : 'text-slate-400 dark:text-slate-500'}`}>SKT Alarm</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-base">{expiringSoonCount > 0 ? '⚠️' : '✅'}</span>
                        <span className={`text-xs font-bold ${expiringSoonCount > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-slate-700 dark:text-slate-300'}`}>{expiringSoonCount} Ürün</span>
                      </div>
                    </div>
                    {sktOpen && (
                      <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-[#1f2937] rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[10px] font-bold text-orange-600 uppercase tracking-widest">Yaklaşan SKT</span>
                          <button onClick={() => navigate('/my-kitchen', { state: { filter: 'expiring' } })} className="text-[10px] font-bold text-primary hover:underline">Tümünü Gör</button>
                        </div>
                        {expiringSoonItems.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {expiringSoonItems.slice(0, 3).map(item => (
                              <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded-xl">
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2">{item.ingredient_name || item.ingredient?.name}</span>
                                <span className="text-[10px] text-orange-600 font-bold whitespace-nowrap">
                                  {(() => {
                                    const expiry = new Date(item.expiry_date);
                                    expiry.setHours(0, 0, 0, 0);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const diff = Math.ceil((expiry - today) / 86400000);
                                    // [Fix] Bu SKT (gün) bilgisi; eskiden `${diff}g` ile "g" eklenince
                                    // miktar (gram) sanılıyordu (örn. "2g" vs gerçekte "2 gün / 9.88kg").
                                    if (diff === 0) return 'Bugün';
                                    if (diff < 0) return 'Süresi geçti';
                                    if (diff === 1) return 'Yarın';
                                    return `${diff} gün`;
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-[11px] text-slate-400 text-center py-2">Riskli ürün bulunmuyor.</p>}
                      </div>
                    )}
                  </div>

                  {/* İsraf Dropdown */}
                  <div className="relative" ref={israfRef}>
                    <div onClick={toggleIsraf} className={`group flex flex-col items-center justify-center rounded-xl px-3 py-1 border min-w-[85px] cursor-pointer hover:shadow-md transition-all text-center ${wastedStats.totalWasted > 0 ? 'bg-red-50/50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30 animate-israf-blink' : 'bg-gray-50 dark:bg-slate-800/50 border-gray-100 dark:border-slate-700'}`}>
                      <span className={`text-[9px] font-bold uppercase tracking-wider ${wastedStats.totalWasted > 0 ? 'text-red-600/70 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>İsraf</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-base">⚠️️</span>
                        <span className="text-xs font-bold text-red-600 dark:text-red-400">-{formatCurrency(wastedStats.totalWasted || 0)}</span>
                      </div>
                    </div>
                    {israfOpen && (
                      <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-[#1f2937] rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest">Son İsraf Kalemleri</span>
                          <button onClick={() => navigate('/my-kitchen', { state: { filter: 'wasted' } })} className="text-[10px] font-bold text-primary hover:underline">Tümünü Gör</button>
                        </div>
                        {wastedItemsList.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {wastedItemsList.slice(0, 3).map(item => (
                              <div key={item.id} className="flex items-center justify-between p-2 bg-red-50/20 dark:bg-red-900/10 rounded-xl">
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2">{item.ingredientName}</span>
                                <span className="text-[10px] text-red-500 font-black">-{formatCurrency(item.purchasePrice)}</span>
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-[11px] text-slate-400 text-center py-2">İsraf kaydı bulunmuyor.</p>}
                      </div>
                    )}
                  </div>

                  {/* Kurtarılan Dropdown */}
                  <div className="relative" ref={rescuedRef}>
                    <div
                      onClick={toggleRescued}
                      className={`group flex flex-col items-center justify-center rounded-xl px-3 py-1 border min-w-[85px] cursor-pointer hover:shadow-md transition-all text-center ${rescuedCount > 0 ? 'bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30 animate-rescued-blink' : 'bg-gray-50 dark:bg-slate-800/50 border-gray-100 dark:border-slate-700'}`}
                    >
                      <span className={`text-[9px] font-bold uppercase tracking-wider ${rescuedCount > 0 ? 'text-green-600/70 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>Kurtarılan</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-base">♻️</span>
                        <span className="text-xs font-bold text-green-700 dark:text-slate-300">
                          {totalSavedValue > 1000 ? `${(totalSavedValue / 1000).toFixed(1)}k ₺` : formatCurrency(totalSavedValue)}
                        </span>
                      </div>
                    </div>
                    {rescuedOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white dark:bg-[#1f2937] rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[10px] font-bold text-green-600 uppercase tracking-widest font-black">KURTARILAN GIDALAR</span>
                          <button onClick={() => navigate('/my-kitchen', { state: { filter: 'rescued' } })} className="text-[10px] font-bold text-red-600 hover:underline">Tümünü Gör</button>
                        </div>
                        {rescuedItems.length > 0 ? (
                          <div className="flex flex-col gap-3">
                            {rescuedItems.slice(0, 3).map(item => (
                              <div key={item.id} className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-2 last:border-0 last:pb-0">
                                <div className="flex flex-col">
                                  <span className="text-[11px] font-black text-slate-700 dark:text-slate-200">{item.ingredient_name || item.ingredient?.name}</span>
                                  <span className="text-[9px] font-bold text-green-600 uppercase">Kazanılan Değer: +{formatCurrency(Math.abs(Number(item.purchase_price)))}</span>
                                </div>
                                <span className="material-symbols-outlined text-[14px] text-green-500">energy_savings_leaf</span>
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-[11px] text-slate-400 text-center py-2">Henüz kurtarılan ürün yok.</p>}
                      </div>
                    )}
                  </div>

                  {/* Değer Dropdown */}
                  <div className="relative" ref={valueRef}>
                    <div onClick={toggleValue} className="group flex flex-col items-center justify-center rounded-xl bg-blue-50/50 dark:bg-blue-900/10 px-3 py-1 border border-blue-100 dark:border-blue-900/30 min-w-[75px] cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 hover:shadow-md transition-all text-center">
                      <span className="text-[9px] font-bold text-blue-600/70 dark:text-blue-400 uppercase tracking-wider">Değer</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-base">💰</span>
                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                          {kitchenValueStats.totalValue > 1000 ? `${(kitchenValueStats.totalValue / 1000).toFixed(1)}k ₺` : formatCurrency(kitchenValueStats.totalValue)}
                        </span>
                      </div>
                    </div>
                    {valueOpen && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-[#1f2937] rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                        <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-3 block">Mutfak Değeri Detayları</span>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-[11px]">
                            <span className="text-slate-500">Mutfak Değeri:</span>
                            <span className="font-bold text-slate-700 dark:text-slate-200">{formatCurrency(kitchenValueStats.activeInventoryValue)}</span>
                          </div>
                          <div className="flex justify-between items-center text-[11px]">
                            <span className="text-slate-500">Tasarruf:</span>
                            <span className="font-bold text-green-600">+{formatCurrency(kitchenValueStats.rescuedValue)}</span>
                          </div>
                          <div className="h-px bg-gray-100 dark:bg-slate-700" />
                          <div className="flex justify-between items-center text-[12px] font-black">
                            <span className="text-slate-700 dark:text-white">Toplam:</span>
                            <span className="text-blue-600">{formatCurrency(kitchenValueStats.totalValue)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Right section: Profile hub */}
            <div className="flex items-center gap-4 lg:gap-6 ml-auto">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => navigate('/notifications')}
                  className="relative p-1.5 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <span className="material-symbols-outlined text-[24px]">notifications</span>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white ring-1 ring-white dark:ring-slate-900">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* 🛒 Aktif Alışveriş Listeleri */}
                <button
                  onClick={() => navigate('/my-shopping-lists', { state: { openHistory: true } })}
                  className="relative p-1.5 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                  title="Alışveriş Listelerim"
                >
                  <span className="material-symbols-outlined text-[24px]">shopping_cart</span>
                  {activeListCount > 0 && (
                    <span className="absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[8px] font-bold text-white ring-1 ring-white dark:ring-slate-900">
                      {activeListCount > 9 ? '9+' : activeListCount}
                    </span>
                  )}
                </button>
              </div>

              <div className="flex flex-col items-end justify-center">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-black text-slate-900 dark:text-white transition-colors"> {chefFirstName} Şef </h2>
                  {isPremiumUser && (
                    <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-white shadow-sm">Premium</span>
                  )}
                </div>

                {/* --- WEB VIEW ICON ALIGNMENT (Exactly Below Name) --- */}
                <div className="flex flex-row-reverse items-center gap-1.5 mt-2">
                  {/* Dark Mode Icon */}
                  <button onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }} className="size-8 bg-white dark:bg-slate-800 rounded-lg shadow-md flex items-center justify-center border border-gray-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer">
                    <span className="material-symbols-outlined text-[18px] text-slate-500 dark:text-slate-400">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
                  </button>

                  {/* Settings Icon */}
                  <button onClick={(e) => { e.stopPropagation(); navigate('/profile'); }} className="size-8 bg-white dark:bg-slate-800 rounded-lg shadow-md flex items-center justify-center border border-gray-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer">
                    <span className="material-symbols-outlined text-[18px] text-slate-500 dark:text-slate-400">settings</span>
                  </button>

                  {/* Shield (Kalkan) Icon - BLUE BACKGROUND */}
                  <button onClick={(e) => { e.stopPropagation(); openModal('kalkan'); }} className="size-8 bg-blue-500 rounded-lg shadow-md flex items-center justify-center border border-blue-400 hover:bg-blue-600 transition-colors cursor-pointer text-white">
                    <span className="material-symbols-outlined text-[18px]">verified_user</span>
                  </button>

                  {/* Baby (Bebek) Icon - GREEN BACKGROUND */}
                  {hasBaby && (
                    <button onClick={(e) => { e.stopPropagation(); openModal('bebek'); }} className="size-8 bg-emerald-500 rounded-lg shadow-md flex items-center justify-center border border-emerald-400 hover:bg-emerald-600 transition-colors cursor-pointer text-[14px]">
                      👶
                    </button>
                  )}
                </div>
              </div>

              <div className="relative group" ref={profileRef}>
                <div onClick={toggleProfile} className="size-14 overflow-hidden rounded-2xl border-2 border-white dark:border-slate-800 shadow-md ring-2 ring-primary/5 transition-transform group-hover:scale-105 cursor-pointer z-[120] relative">
                  <img alt="Şef Avatar" className="h-full w-full object-cover" src={avatar || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=ee2b2b&color=fff`} />
                </div>

                {/* Profile Menu */}
                <div className={`absolute right-0 top-full mt-4 w-56 origin-top-right rounded-2xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 p-2 flex flex-col gap-1 transition-all duration-150 transform z-[120] ${profileOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95 pointer-events-none'}`}>
                  <button onClick={() => { setProfileOpen(false); navigate('/profile'); }} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <i className="fa-solid fa-user text-[18px]"></i>
                    Profili Düzenle
                  </button>
                  {user?.isAdmin && (
                    <button onClick={() => { setProfileOpen(false); navigate('/admin'); }} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                      <i className="fa-solid fa-gauge-high text-[18px]"></i>
                      Admin Paneli
                    </button>
                  )}
                  <div className="h-px bg-neutral-100 dark:bg-slate-700 mx-2" />
                  <button onClick={async () => { setProfileOpen(false); await logout(); }} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                    <i className="fa-solid fa-right-from-bracket text-[18px]"></i>
                    Çıkış Yap
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE VIEW HEADER */}
      <header className="flex md:hidden sticky top-0 z-[110] bg-white dark:bg-[#1a1a1a] shadow-md w-full transition-all flex-col border-b dark:border-slate-700">
        <div className="flex items-center justify-between px-3 h-[110px]">
          {showMenuButton ? (
            <button onClick={openMobile} className="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 active:scale-90 transition-transform">
              <span className="material-icons-round text-[34px]">menu</span>
            </button>
          ) : (
            <div className="w-10" /> // Spacer
          )}

          <div className="flex-1 flex items-center justify-center gap-2" ref={dashboardToggleRef}>
            {!isAssistantChef && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAutoOpen(false); // Cancel auto-close on manual interaction
                  setDashboardOpen(!dashboardOpen);
                }}
                className="flex flex-col items-center bg-gray-50 dark:bg-slate-700 px-3 py-1 rounded-full border border-gray-100 dark:border-slate-600"
              >
                <span className="text-[10px] font-bold text-red-500 dark:text-red-400">Mutfak Panelim</span>
                <span className={`material-symbols-outlined text-[10px] text-primary transition-transform duration-300 ${dashboardOpen ? 'rotate-180' : ''}`}>expand_more</span>
              </button>
            )}
          </div>

          {/* Desktop-style Profile Section on Mobile */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Notification & Shop Vertical Stack (Mobile) */}
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-1 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white ring-1 ring-white dark:ring-slate-900">
                    {unreadCount > 9 ? '9' : unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate('/my-shopping-lists', { state: { openHistory: true } })}
                className="relative p-1 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
                {activeListCount > 0 && (
                  <span className="absolute top-0 right-0 flex h-3 w-3 items-center justify-center rounded-full bg-orange-500 text-[7px] font-bold text-white ring-1 ring-white dark:ring-slate-900">
                    {activeListCount > 9 ? '9' : activeListCount}
                  </span>
                )}
              </button>
            </div>

            {/* Profile Info & Action Icons Stack */}
            <div className="flex flex-col items-end justify-center gap-1.5">
              <div className="flex items-center gap-1.5">
                <h2 className="text-xs font-bold text-slate-900 dark:text-white whitespace-nowrap">{chefFirstName} Şef</h2>
                {isPremiumUser && (
                  <span className="rounded bg-gradient-to-r from-primary to-orange-600 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white shadow-sm">Premium</span>
                )}
              </div>

              {/* --- MOBILE ICON SEQUENCE (Exactly Below Name) --- */}
              <div className="flex items-center gap-1">
                {hasBaby && (
                  <button onClick={(e) => { e.stopPropagation(); openModal('bebek'); }} className="size-7 bg-emerald-500 rounded-lg shadow-sm flex items-center justify-center border border-emerald-400 text-xs text-center text-white">👶</button>
                )}
                <button onClick={(e) => { e.stopPropagation(); openModal('kalkan'); }} className="size-7 bg-blue-500 rounded-lg shadow-sm flex items-center justify-center border border-blue-400 text-white">
                  <span className="material-symbols-outlined text-[14px]">verified_user</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); navigate('/profile'); }} className="size-7 bg-white dark:bg-slate-800 rounded-lg shadow-sm flex items-center justify-center border dark:border-slate-700">
                  <span className="material-symbols-outlined text-[14px] text-slate-500 dark:text-slate-400">settings</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }} className="size-7 bg-white dark:bg-slate-800 rounded-lg shadow-sm flex items-center justify-center border dark:border-slate-700">
                  <span className="material-symbols-outlined text-[14px] text-slate-500 dark:text-slate-400">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
                </button>
              </div>
            </div>

            {/* Avatar & Menu */}
            <div className="relative shrink-0" ref={profileMobileRef}>
              <div onClick={() => setProfileOpen(!profileOpen)} className="size-12 overflow-hidden rounded-2xl border-2 border-white dark:border-slate-800 shadow-md ring-2 ring-primary/5 transition-transform cursor-pointer">
                <img alt="Şef Avatar" className="h-full w-full object-cover" src={avatar || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=ee2b2b&color=fff`} />
              </div>

              {/* Mobile Profile Menu */}
              <div className={`absolute right-0 top-full mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 p-2 flex flex-col gap-1 transition-all duration-150 transform z-[120] ${profileOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95 pointer-events-none'}`}>
                <button onClick={(e) => { e.stopPropagation(); setProfileOpen(false); navigate('/profile'); }} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700">
                  <i className="fa-solid fa-user"></i> Profili Düzenle
                </button>
                {user?.isAdmin && (
                  <button onClick={(e) => { e.stopPropagation(); setProfileOpen(false); navigate('/admin'); }} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5 dark:hover:bg-primary/10">
                    <i className="fa-solid fa-gauge-high"></i> Admin Paneli
                  </button>
                )}
                <div className="h-px bg-neutral-100 dark:bg-slate-700 mx-2" />
                <button onClick={async (e) => { e.stopPropagation(); setProfileOpen(false); await logout(); }} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <i className="fa-solid fa-right-from-bracket"></i> Çıkış Yap
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Dashboard Panel */}
      <div
        ref={dashboardRef}
        className={`overflow-hidden transition-all duration-500 ease-in-out bg-gray-50 dark:bg-slate-800/50 ${dashboardOpen ? 'max-h-[500px]' : 'max-h-0'}`}
      >
        <div className="p-4 grid grid-cols-3 gap-2">
          {/* 1. Envanter */}
          <div onClick={() => openModal('envanter')} className="bg-white dark:bg-slate-700 rounded-xl p-3 border border-gray-100 dark:border-slate-600 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-600 transition-all">
            <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase block">Envanter</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">🍅</span>
              <p className="text-sm font-black text-gray-800 dark:text-gray-200">{inventoryCount}</p>
            </div>
          </div>

          {/* 2. Bebek Güvenliği */}
          <div onClick={() => openModal('bebek')} className="bg-white dark:bg-slate-700 rounded-xl p-3 border border-blue-100 dark:border-blue-900/30 text-center cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
            <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase block">Bebek</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">🛡️</span>
              <p className="text-sm font-black text-blue-700 dark:text-blue-300">Güvenli</p>
            </div>
          </div>

          {/* 3. SKT Alarm */}
          <div onClick={() => openModal('skt')} className={`bg-white dark:bg-slate-700 rounded-xl p-3 border text-center cursor-pointer hover:shadow-md transition-all ${expiringSoonCount > 0 ? 'border-orange-100 dark:border-orange-900/30 hover:bg-orange-50 dark:hover:bg-orange-900/20' : 'border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'}`}>
            <span className={`text-[9px] font-bold uppercase block ${expiringSoonCount > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500'}`}>SKT Alarm</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">{expiringSoonCount > 0 ? '⚠️' : '✅'}</span>
              <p className={`text-sm font-black ${expiringSoonCount > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400'}`}>{expiringSoonCount}</p>
            </div>
          </div>

          {/* 4. İsraf */}
          <div onClick={() => openModal('israf')} className={`bg-white dark:bg-slate-700 rounded-xl p-3 border text-center cursor-pointer hover:shadow-md transition-all ${wastedStats.totalWasted > 0 ? 'border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/20' : 'border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'}`}>
            <span className={`text-[9px] font-bold uppercase block ${wastedStats.totalWasted > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`}>İsraf</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">⚠️</span>
              <p className="text-sm font-black text-red-600 dark:text-red-400">-{formatCurrency(wastedStats.totalWasted)}</p>
            </div>
          </div>

          {/* 5. Kurtarılan */}
          <div onClick={() => openModal('tasarruf')} className={`bg-white dark:bg-slate-700 rounded-xl p-3 border text-center cursor-pointer hover:shadow-md transition-all ${rescuedCount > 0 ? 'border-green-100 dark:border-green-900/30 hover:bg-green-50 dark:hover:bg-green-900/20' : 'border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'}`}>
            <span className={`text-[9px] font-bold uppercase block ${rescuedCount > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>Kurtarılan</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">♻️</span>
              <p className="text-sm font-black text-green-600 dark:text-green-400">+{formatCurrency(totalSavedValue)}</p>
            </div>
          </div>

          {/* 6. Değer */}
          <div onClick={() => openModal('deger')} className="bg-white dark:bg-slate-700 rounded-xl p-3 border border-blue-100 dark:border-blue-900/30 text-center cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
            <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase block">Değer</span>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-base">💰</span>
              <p className="text-sm font-black text-blue-700 dark:text-blue-300">{formatCurrency(kitchenValueStats.totalValue)}</p>
            </div>
          </div>
        </div>
      </div>


      {/* INFO MODAL */}
      {
        modalOpen && modalContent && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 modal-overlay" onClick={closeModal}>
            <div
              className="bg-white dark:bg-[#1f2937] w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl border border-white/20 animate-in fade-in zoom-in duration-200 relative"
              onClick={e => e.stopPropagation()}
            >
              {/* Simple Close Icon */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors z-20"
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>

              <div className="p-6 text-center max-h-[85vh] overflow-y-auto no-scrollbar">
                <div className="text-5xl mb-4 drop-shadow-sm">{modalContent.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 tracking-tight">{modalContent.title}</h3>
                <p className="text-[13px] text-slate-500 dark:text-gray-400 leading-relaxed mb-6 px-2 font-medium whitespace-pre-line">{modalContent.desc}</p>

                {/* Added Dynamic List inside Modal */}
                {modalContent.type === 'skt' && expiringSoonItems.length > 0 && (
                  <div className="flex flex-col gap-2 text-left mb-8">
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2 mb-1">Kritik Stok Listesi</span>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto no-scrollbar pr-1">
                      {expiringSoonItems.slice(0, 5).map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50/80 dark:bg-slate-800/80 rounded-2xl border border-gray-100 dark:border-slate-700">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{item.ingredient_name || item.ingredient?.name}</span>
                            <span className="text-[10px] text-orange-600 font-bold">{Math.ceil((new Date(item.expiry_date) - new Date().setHours(0, 0, 0, 0)) / 86400000)} gün kaldı</span>
                          </div>
                          <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">{item.remaining_amount} {item.unit}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => { closeModal(); navigate('/my-kitchen', { state: { filter: 'expiring' } }); }} className="mt-2 text-xs font-bold text-primary hover:underline text-center w-full">Tüm Mutfağı Gör →</button>
                  </div>
                )}

                {modalContent.type === 'israf' && wastedItemsList.length > 0 && (
                  <div className="flex flex-col gap-2 text-left mb-8">
                    <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest px-2 mb-1">Son İsraf Kalemleri</span>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto no-scrollbar pr-1">
                      {wastedItemsList.slice(0, 5).map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-red-50/30 dark:bg-red-900/10 rounded-2xl border border-red-100/50 dark:border-red-900/20">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{item.ingredientName}</span>
                            <span className="text-[10px] font-bold text-red-500">-{Math.abs(item.purchasePrice)} ₺</span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-medium">{new Date(item.wastedAt).toLocaleDateString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => { closeModal(); navigate('/my-kitchen', { state: { filter: 'wasted' } }); }} className="mt-2 text-xs font-bold text-red-600 hover:underline text-center w-full">Tüm Geçmişi Gör →</button>
                  </div>
                )}

                {modalContent.type === 'tasarruf' && rescuedItems.length > 0 && (
                  <div className="flex flex-col gap-2 text-left mb-8">
                    <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest px-2 mb-1">Kurtarılan Ürünler</span>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto no-scrollbar pr-1">
                      {rescuedItems.slice(0, 5).map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-green-50/30 dark:bg-green-900/10 rounded-2xl border border-green-100/50 dark:border-green-900/20">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{item.ingredient_name || item.ingredient?.name}</span>
                            <span className="text-[10px] font-bold text-green-600">+{Math.abs(Number(item.purchase_price))} ₺</span>
                          </div>
                          <span className="material-symbols-outlined text-green-500 text-sm">energy_savings_leaf</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => { closeModal(); navigate('/my-kitchen', { state: { filter: 'rescued' } }); }} className="mt-2 text-xs font-bold text-green-600 hover:underline text-center w-full">Mutfak Panosu →</button>
                  </div>
                )}

                {modalContent.type === 'deger' && (
                  <div className="p-4 space-y-4 mb-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-gray-100 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center text-blue-500 shadow-sm">
                          <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                        </div>
                        <div className="text-left">
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">Stok Değeri</p>
                          <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">Depodaki Ürünler</p>
                        </div>
                      </div>
                      <div className="text-right text-base font-black text-blue-600 dark:text-blue-400">
                        {formatCurrency(kitchenValueStats.activeInventoryValue)}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-green-50/50 dark:bg-green-900/10 rounded-2xl border border-green-100 dark:border-green-900/20">
                      <div className="flex items-center gap-3">
                        <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center text-green-500 shadow-sm">
                          <span className="text-[20px] leading-none">♻️</span>
                        </div>
                        <div className="text-left">
                          <p className="text-[10px] text-green-600 dark:text-green-500 font-bold uppercase tracking-widest leading-none mb-1">Tasarruf Değeri</p>
                          <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">Kurtarılan Kazanç</p>
                        </div>
                      </div>
                      <div className="text-right text-base font-black text-green-600 dark:text-green-400">
                        +{formatCurrency(kitchenValueStats.rescuedValue)}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-dashed border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between px-2">
                        <p className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">Toplam Mutfak Değeri</p>
                        <p className="text-xl font-black text-blue-600 dark:text-blue-400">
                          {formatCurrency(kitchenValueStats.totalValue)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    closeModal();
                    if (modalContent.type === 'envanter' || modalContent.type === 'skt' || modalContent.type === 'israf' || modalContent.type === 'tasarruf' || modalContent.type === 'deger') {
                      navigate('/my-kitchen', {
                        state: {
                          filter: modalContent.type === 'skt' ? 'expiring' :
                            modalContent.type === 'israf' ? 'wasted' :
                              modalContent.type === 'tasarruf' ? 'rescued' : null
                        }
                      });
                    }
                  }}
                  className="w-full py-4 bg-slate-900 dark:bg-primary text-white font-bold rounded-2xl shadow-xl hover:opacity-90 transition-all active:scale-[0.98]"
                >
                  {(modalContent.type === 'envanter' || modalContent.type === 'skt' || modalContent.type === 'israf' || modalContent.type === 'tasarruf' || modalContent.type === 'deger') ? 'MUTFAĞA GİT →' : 'ANLADIM ŞEF!'}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default TopBar;
